import { useNavigate } from "react-router-dom";
import React, { ComponentProps, useEffect, useRef, useState } from "react";

// MUI components
import { Box, Button, Divider, Link, Paper, PaperProps, Typography } from "@mui/material";

// Components
import CustomIcon from "components/CustomIcon";
import { IndustryTag } from "pages/Projects/styled-components";
import CantFindData from "components/Pro/CantFindData";
import ResultsNotFound from "components/ResultsNotFound/ResultsNotFound";
import { useSnackBar } from "components/SnackBar";

// Hooks
import { useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "app/hooks";

// Services
import {
    columnsOrganisationSearchOS,
    CompanySearchHub,
    ClientHubOrganisationSearchOS,
    convertOSCompanyResponse,
    fetchCompanyDiscoverService,
    getCompanyPayload,
    RoleBundleResponse,
    RoleBundle,
    createClientHub,
    OrgsCompany,
} from "services";

// Storetore
import { getLoaderState, toggleLoader } from "store/slice/UI";

// Utils
import { camelCase, debounce, isEmpty } from "lodash";

// Enums
import icons from "enums/icons";
import { urlPaths } from "enums/urlPaths";

// Styles
import useStyles from "./HubManagementNew.styles";

// Types
import { TSelectedPermissions } from "./components/Permissions/Permissions";
import { unwrapResult } from "@reduxjs/toolkit";
import { fetchPermissionsRefBundleThunk } from "store/slice/sysadmin/permissions/permissionsSlice";

const INITIAL_ORGS: {
    data: CompanySearchHub[];
    status: string;
} = { data: [], status: "pristine" };

const INITIAL_PERMISSIONS = {
    ldPermission: {} as RoleBundle,
    pacePermission: {} as RoleBundle,
};

const useHubManagementNew = (): {
    open: boolean;
    options: typeof INITIAL_ORGS;
    bodyContainerRef: React.MutableRefObject<HTMLDivElement | null>;
    selectedHub: CompanySearchHub | null;
    selectedPermission: TSelectedPermissions;
    shouldOpenSnackBar: boolean;
    permissionsBundle: RoleBundleResponse;
    isFetching: boolean;
    disablePermissions: boolean;
    handleValidateCRMLink: (e: React.MouseEvent<HTMLElement>) => void;
    handleExitPage: () => void;
    handleGoNext: () => Promise<void>;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    getOptionLabel: (option: CompanySearchHub) => string;
    onChange: (e: React.SyntheticEvent, value: CompanySearchHub | null) => void;
    onInputChange: (e: React.SyntheticEvent, value: string, reason: string) => void;
    handleClickClearFilter: () => void;
    renderOption: (props: React.HTMLAttributes<HTMLLIElement>, option: CompanySearchHub) => JSX.Element;
    PaperComponent: (props: PaperProps) => JSX.Element;
    handleSelectPermission: (permission: RoleBundle, type: keyof TSelectedPermissions) => Promise<void>;
    handleCloseSnackBar: (
        event: React.MouseEvent<HTMLElement>,
        reason?: "backdropClick" | "escapeKeyDown",
        callback?: () => void
    ) => void;
    handleCRMGoBack: (e: React.MouseEvent<HTMLElement>) => void;
    onClickOrgDomain: (event: React.MouseEvent) => void;
} => {
    const intl = useIntl();
    const classes = useStyles();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const isFetching = useAppSelector(getLoaderState);

    const [options, setOptions] = useState(INITIAL_ORGS);
    const [selectedHub, setSelectedHub] = useState<CompanySearchHub | null>(null);
    const [open, setOpen] = useState(false);
    const [hasValidCRMLink] = useState(false);
    const [disablePermissions, setDisablePermissions] = useState(isEmpty(selectedHub));

    const { shouldOpenSnackBar, setPopOverAnchorEl, handleOpenSnackBar, handleCloseSnackBar } = useSnackBar();
    const bodyContainerRef = useRef<HTMLDivElement | null>(null);

    const [permissionsBundle, setPermissionsBundle] = useState<RoleBundleResponse>({} as RoleBundleResponse);
    const [selectedPermission, setSelectedPermission] = useState<TSelectedPermissions>(INITIAL_PERMISSIONS);

    const handleSelectPermission = async (permission: RoleBundle, type: keyof TSelectedPermissions) => {
        const newPermissions = { ...selectedPermission, [type]: permission };

        setSelectedPermission(newPermissions);
    };

    const getOptionLabel = (option: CompanySearchHub): string => option.name || "";

    const onChange = (e: React.SyntheticEvent, value: CompanySearchHub | null) => {
        if (value) {
            const crmIdentifier = value.identifiers.find((identifier) => identifier.type === "crm");
            if (!crmIdentifier) {
                setSelectedHub(null);
            } else {
                setSelectedHub(value);
                setDisablePermissions(false);
            }
        }

        setOpen(false);
    };

    const fetchOrganisations = debounce(async (value: string) => {
        const searchParams = {
            termField: "name",
            termFilter: value,
            columns: columnsOrganisationSearchOS,
            meta: {
                page_size: 25,
                page_no: 1,
            },
        };

        setOptions((prevValue) => ({ ...prevValue, status: "fetching" }));

        try {
            const response = await fetchCompanyDiscoverService<ClientHubOrganisationSearchOS>(
                getCompanyPayload(searchParams)
            );
            const resultsConverted = response.data.results.length
                ? response.data.results.map((company) => convertOSCompanyResponse(company))
                : [];

            setOptions({ data: resultsConverted, status: "done" });
        } catch (error) {
            setOptions({ data: [], status: "done" });
            console.error(error);
        }
    }, 500);

    const onInputChange = (e: React.SyntheticEvent, value: string, reason: string) => {
        if (value.length < 2 || reason === "clear" || reason === "reset") {
            setOptions(INITIAL_ORGS);
            return;
        }

        fetchOrganisations(value);
    };

    const handleClickClearFilter = () => {
        setSelectedHub(null);
        setSelectedPermission(INITIAL_PERMISSIONS);
        setDisablePermissions(true);
    };

    const onClickOrgDomain = (event: React.MouseEvent) => event.stopPropagation();

    const handleAddNewClientHub = (option: CompanySearchHub | null) => {
        setSelectedHub(option);
        setDisablePermissions(false);
        setOpen(false);
    };

    const handleGotoClientHub = (option: OrgsCompany) => {
        navigate(`${urlPaths.HubManagementEditClient}/${option.id}`);
    };

    const handleValidateCRMLink = (e: React.MouseEvent<HTMLElement>) => {
        if (!hasValidCRMLink) {
            setPopOverAnchorEl(document.body);
            handleOpenSnackBar(e);
        }
    };

    const handleCRMGoBack = (e: React.MouseEvent<HTMLElement>) => {
        handleCloseSnackBar(e);
        setSelectedHub(null);
        setDisablePermissions(true);
    };

    const handleExitPage = () => {
        navigate(urlPaths.HubManagement);
    };

    const onMouseDown = (ev: React.MouseEvent) => ev.preventDefault();

    const handleGoNext = async () => {
        if (selectedHub === null) return;

        const permissions = Object.values(selectedPermission).flatMap((p) => p?.name);

        try {
            dispatch(toggleLoader(true));
            const { data: clientHubData } = await createClientHub({
                name: selectedHub.name,
                pay_wall_role_masks: permissions,
                company_id: selectedHub.id,
            });
            navigate(`${urlPaths.HubManagementAddUsers}/${clientHubData.id}`);
        } catch (error) {
            console.error(error);
        }
    };

    const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: CompanySearchHub) => {
        const domain = option.domains[0]?.domain || "";
        const clientHub = option.orgs[0];
        const subOrgCount = clientHub?.sub_orgs?.length;

        const industry = option.subindustry?.[0]?.gics_subindustry.gics_sector.name || "";
        const industryType = camelCase(option.subindustry?.[0]?.gics_subindustry.gics_sector.name) as ComponentProps<
            typeof IndustryTag
        >["type"];

        return (
            <Box key={option.id}>
                <Box className={classes.companyResultsWrapper}>
                    <Box sx={{ display: "grid" }}>
                        <Typography className={classes.companyName}>{option.name}</Typography>

                        <Box display="flex">
                            <IndustryTag
                                label={industry}
                                icon={<CustomIcon sx={{ fontSize: 14 }} icon={icons.sector} />}
                                type={industryType}
                            />

                            {Boolean(subOrgCount) && (
                                <IndustryTag
                                    style={{ fontWeight: 600 }}
                                    label={`${subOrgCount} client groups`}
                                    icon={<CustomIcon sx={{ fontSize: 16 }} icon={icons.mainMenuDashboard} />}
                                    type="clientGroups"
                                />
                            )}
                        </Box>

                        <Link
                            className={classes.companyLink}
                            target="_blank"
                            href={`http://${domain}`}
                            onClick={onClickOrgDomain}
                            rel="noopener noreferrer"
                        >
                            {domain}
                        </Link>
                    </Box>
                    <Box sx={{ alignContent: "center" }}>
                        <Button
                            size="small"
                            variant={clientHub ? "contained" : "outlined"}
                            {...(clientHub ? { color: "secondary" } : {})}
                            onClick={
                                clientHub ? () => handleGotoClientHub(clientHub) : () => handleAddNewClientHub(option)
                            }
                            sx={{
                                backgroundColor: clientHub ? "neutrals.light" : "transparent",
                                height: 30,
                                px: 2,
                                "&:hover": {
                                    backgroundColor: clientHub ? "neutrals.light" : "transparent",
                                },
                            }}
                            endIcon={
                                <CustomIcon
                                    sx={{ fontSize: 40 }}
                                    icon={clientHub ? icons.circledArrowOutlined : icons.circledArrow}
                                />
                            }
                        >
                            {intl.formatMessage({
                                id: clientHub
                                    ? "hubManagement.button.existingClientHub"
                                    : "hubManagement.button.addClientHub",
                            })}
                        </Button>
                    </Box>
                </Box>
                <Divider flexItem orientation="vertical" className={classes.divider} />
            </Box>
        );
    };

    const PaperComponent = (props: PaperProps) => (
        <Box marginTop={1}>
            {Boolean(!options.data.length) && options.status === "done" && (
                <Paper className={classes.paper}>
                    <ResultsNotFound
                        imageSize={253}
                        showContact={false}
                        message={[
                            { id: "hubManagement.notFound.message1" },
                            { id: "hubManagement.notFound.message2" },
                            { id: "hubManagement.notFound.message3" },
                            { id: "hubManagement.notFound.message4" },
                        ]}
                    />
                </Paper>
            )}

            {Boolean(options.data.length) && options.status === "done" && (
                <Paper className={classes.paper}>
                    <Box>{props.children}</Box>
                    <Divider flexItem orientation="vertical" className={classes.divider} />
                    <Box className={classes.linksWrapper}>
                        <Link
                            sx={{ marginRight: "8px", fontSize: 12 }}
                            target="_blank"
                            href="https://data-updates.drxdata.com/company"
                            onMouseDown={onMouseDown}
                            rel="noopener noreferrer"
                        >
                            <Typography>{intl.formatMessage({ id: "hubManagement.searchBox.linkText2" })}</Typography>
                        </Link>

                        <CantFindData
                            style={{ fontSize: 12, textDecoration: "underline", marginLeft: "8px" }}
                            text={
                                <Typography>
                                    {intl.formatMessage({ id: "hubManagement.searchBox.linkText1" })}
                                </Typography>
                            }
                        />
                    </Box>
                </Paper>
            )}
        </Box>
    );

    // --------------------------------------------------------------------------------------------------------------
    // Initial Fetch
    // --------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        const initialFetch = async () => {
            dispatch(toggleLoader(true));
            const permissionsBundle = unwrapResult(await dispatch(fetchPermissionsRefBundleThunk()));
            setPermissionsBundle(permissionsBundle);
            dispatch(toggleLoader(false));
        };
        initialFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        open,
        options,
        bodyContainerRef,
        selectedHub,
        selectedPermission,
        shouldOpenSnackBar,
        permissionsBundle,
        isFetching,
        disablePermissions,
        handleValidateCRMLink,
        handleExitPage,
        handleGoNext,
        setOpen,
        getOptionLabel,
        onChange,
        onInputChange,
        handleClickClearFilter,
        renderOption,
        PaperComponent,
        handleSelectPermission,
        handleCloseSnackBar,
        handleCRMGoBack,
        onClickOrgDomain,
    };
};

export default useHubManagementNew;
